import { EmptyView, ErrorState, LoadingState, Timeline } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { useFeatureFlagAuditQuery } from "../services/useFeatureFlagAuditQuery";

type Props = {
  flagId: string;
};

/** A flag's audit trail as a timeline: when, what, the detail and who. */
export function FeatureFlagAuditPanel({ flagId }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const { data, isLoading, error, refetch, isRefetching } = useFeatureFlagAuditQuery(flagId, true);

  if (isLoading) {
    return <LoadingState title={t(keys.shell.loadingTitle, { page: t("featureFlags.historyPage") })} rows={0} />;
  }
  if (error || !data) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page: t("featureFlags.historyPage") })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={isRefetching ? t(keys.shell.retrying) : t(keys.shell.retry)}
        retrying={isRefetching}
      />
    );
  }
  if (data.length === 0) {
    return <EmptyView title={t(keys.featureFlags.historyEmpty)} sub={t("featureFlags.historyEmptySub")} />;
  }
  return (
    <Timeline
      items={data.map((entry) => ({
        id: entry.id,
        // The rail's time column is narrow: the clock goes there, the day
        // leads the note.
        time: formatTime(entry.changedAt, locale),
        label: entry.action,
        note: [formatDate(entry.changedAt, locale), entry.detail, entry.actorEmail].filter(Boolean).join(" · "),
      }))}
    />
  );
}

function formatTime(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatDate(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(locale, { dateStyle: "medium" });
}
